import { all, fork, put, takeEvery, call } from "redux-saga/effects";
import { SagaIterator } from "@redux-saga/core";

// helpers
import {
  getExpeditions as getExpeditionList,
  createExpeditionApi,
  getExpedition as getExpeditionApi,
  updateExpeditionApi,
  deleteExpeditionApi,
  updateStatusExpeditionApi,
} from "../../helpers/";

// constants
import { ExpeditionsActionTypes } from "./constants";
import {
  createExpeditionError,
  createExpeditionSuccess,
  expeditionsGetListError,
  expeditionsGetListSuccess,
  getExpeditionSuccess,
  getExpeditionError,
  updateExpeditionSuccess,
  updateExpeditionError,
  deleteExpeditionSuccess,
  deleteExpeditionError,
  updateStatusExpeditionSuccess,
  updateStatusExpeditionError,
} from "./actions";

interface ExpeditionData {
  payload: {
    limit: number;
    skip: number;
    keyword?: string;
    owner?: string;
    country?: string;
    startDate?: string;
    endDate?: string;
  };
  type: string;
}

interface ExpeditionCreateData {
  payload: {
    country: string;
    comment: string;
    description: string;
    expeditionDate: Date;
    from: any;
    mode: string;
    name: string;
    price: number;
    productImage: string;
    quantity: number;
    transporterName: string;
    transporterPhone: string;
    weight: number;
  };
  type: string;
}

interface ExpeditionUpdateData {
  payload: {
    id: string;
    comment: string;
    description: string;
    expeditionDate: Date;
    from: any;
    mode: string;
    name: string;
    price: number;
    productImage: string;
    quantity: number;
    transporterName: string;
    transporterPhone: string;
    weight: number;
    damaged?: number;
  };
  type: string;
}

interface ExpeditionDeleteData {
  payload: {
    ids: string[];
  };
  type: string;
}

interface GetExpeditionData {
  payload: {
    id: string;
  };
  type: string;
}

function* getExpeditions({
  payload: { limit, skip, keyword, owner, country, startDate, endDate },
}: ExpeditionData): SagaIterator {
  try {
    const params: any = { limit, skip, keyword, owner, country };
    if (startDate) params.startDate = startDate;
    if (endDate) params.endDate = endDate;
    const response = yield call(getExpeditionList, params);
    // Voir orders/saga : ignore 403 silencieux + data null pour ne pas
    // écraser la liste courante.
    if ((response as any)?.__handled403) return;
    const Expeditions = response.data;
    if (Expeditions == null) return;
    yield put(
      expeditionsGetListSuccess(
        ExpeditionsActionTypes.GET_LIST_EXPEDITIONS,
        Expeditions
      )
    );
  } catch (error: any) {
    yield put(
      expeditionsGetListError(
        ExpeditionsActionTypes.GET_LIST_EXPEDITIONS,
        error
      )
    );
  }
}

function* createExpedition({
  payload: {
    country,
    comment,
    description,
    expeditionDate,
    from,
    mode,
    name,
    price,
    productImage,
    quantity,
    transporterName,
    transporterPhone,
    weight,
  },
}: ExpeditionCreateData): SagaIterator {
  try {
    const response = yield call(createExpeditionApi, {
      country,
      comment,
      description,
      expeditionDate,
      from,
      mode,
      name,
      price,
      productImage,
      quantity,
      transporterName,
      transporterPhone,
      weight,
    });
    const expedition = response.data;
    yield put(
      createExpeditionSuccess(
        ExpeditionsActionTypes.CREATE_EXPEDITION,
        expedition
      )
    );
  } catch (error: any) {
    const errorMessage =
      typeof error === "string" ? error : error.response?.data;
    yield put(
      createExpeditionError(
        ExpeditionsActionTypes.CREATE_EXPEDITION,
        errorMessage
      )
    );
  }
}

function* updateExpedition({
  payload: {
    id,
    comment,
    description,
    expeditionDate,
    from,
    mode,
    name,
    price,
    productImage,
    quantity,
    transporterName,
    transporterPhone,
    weight,
    damaged,
  },
}: ExpeditionUpdateData): SagaIterator {
  try {
    const response = yield call(updateExpeditionApi, {
      id,
      comment,
      description,
      expeditionDate,
      from,
      mode,
      name,
      price,
      productImage,
      quantity,
      transporterName,
      transporterPhone,
      weight,
      damaged,
    });
    const expedition = response.data;
    yield put(
      updateExpeditionSuccess(
        ExpeditionsActionTypes.UPDATE_EXPEDITION,
        expedition
      )
    );
  } catch (error: any) {
    const errorMessage =
      typeof error === "string" ? error : error.response?.data;
    yield put(
      updateExpeditionError(
        ExpeditionsActionTypes.UPDATE_EXPEDITION,
        errorMessage
      )
    );
  }
}

function* updateStatusExpedition({
  payload: { id },
}: ExpeditionUpdateData): SagaIterator {
  try {
    const response = yield call(updateStatusExpeditionApi, {
      id,
    });
    const expedition = response.data;
    yield put(
      updateStatusExpeditionSuccess(
        ExpeditionsActionTypes.UPDATE_STATUS_EXPEDITION,
        expedition
      )
    );
  } catch (error: any) {
    const errorMessage =
      typeof error === "string" ? error : error.response?.data;
    yield put(
      updateStatusExpeditionError(
        ExpeditionsActionTypes.UPDATE_STATUS_EXPEDITION,
        errorMessage
      )
    );
  }
}

function* deleteExpedition({
  payload: { ids },
}: ExpeditionDeleteData): SagaIterator {
  try {
    const response = yield call(deleteExpeditionApi, {
      ids,
    });
    const expedition = response.data;
    yield put(
      deleteExpeditionSuccess(
        ExpeditionsActionTypes.DELETE_EXPEDITION,
        expedition
      )
    );
  } catch (error: any) {
    const errorMessage =
      typeof error === "string" ? error : error.response?.data;
    yield put(
      deleteExpeditionError(
        ExpeditionsActionTypes.DELETE_EXPEDITION,
        errorMessage
      )
    );
  }
}

function* getExpedition({ payload: { id } }: GetExpeditionData): SagaIterator {
  try {
    const response = yield call(getExpeditionApi, { id });
    const expedition = response.data;
    yield put(
      getExpeditionSuccess(ExpeditionsActionTypes.GET_EXPEDITION, expedition)
    );
  } catch (error: any) {
    const errorMessage =
      typeof error === "string" ? error : error.response?.data;
    yield put(
      getExpeditionError(ExpeditionsActionTypes.GET_EXPEDITION, errorMessage)
    );
  }
}

export function* watchGetExpeditions() {
  yield takeEvery(ExpeditionsActionTypes.GET_LIST_EXPEDITIONS, getExpeditions);
}

export function* watchCreateExpeditions() {
  yield takeEvery(ExpeditionsActionTypes.CREATE_EXPEDITION, createExpedition);
}

export function* watchUpdateExpeditions() {
  yield takeEvery(ExpeditionsActionTypes.UPDATE_EXPEDITION, updateExpedition);
}

export function* watchUpdateStatusExpeditions() {
  yield takeEvery(
    ExpeditionsActionTypes.UPDATE_STATUS_EXPEDITION,
    updateStatusExpedition
  );
}

export function* watchDeleteExpeditions() {
  yield takeEvery(ExpeditionsActionTypes.DELETE_EXPEDITION, deleteExpedition);
}

export function* watchGetExpedition() {
  yield takeEvery(ExpeditionsActionTypes.GET_EXPEDITION, getExpedition);
}

function* expeditionsSaga() {
  yield all([
    fork(watchGetExpeditions),
    fork(watchCreateExpeditions),
    fork(watchGetExpedition),
    fork(watchUpdateExpeditions),
    fork(watchDeleteExpeditions),
    fork(watchUpdateStatusExpeditions),
  ]);
}

export default expeditionsSaga;
